import {UseQueryOptions} from '@tanstack/react-query';
import {useEffect} from 'react';
import {useWrapRef} from '../common/useWrapRef.hook';
import {
  ReactQueryFetcherChildrenType,
  ReactQueryGetKeyVariablesRequired,
} from './CodegenReactQueryType.type';
import {ReactQueryFetcherChildren} from './ReactQueryFetcherChildren';
import {useRemoveCacheAfterUnmount} from './useRemoveCacheAfterUnmount';

interface Props<QueryData> {
  ignoreLoading?: boolean;
  ignoreFetching?: boolean;
  loadingWrapperClassName?: string;
  shouldRemoveCacheAfterUnmount?: boolean;
  children?: ReactQueryFetcherChildrenType<QueryData>;
  onSuccess?: (data: QueryData) => void;
  onError?: (error: any) => void;
}

type ExtractOptions<O> = O extends Omit<
  UseQueryOptions<infer Data, any, any, any>,
  'queryKey'
>
  ? Data
  : never;

export function ReactQueryFetcher<
  UseQueryHook extends ((...args: any) => any) &
    ReactQueryGetKeyVariablesRequired<Parameters<UseQueryHook>[0]>,
>({
  children,
  reactQuery,
  variables,
  options,
  ignoreLoading,
  ignoreFetching,
  loadingWrapperClassName,
  shouldRemoveCacheAfterUnmount,
  onSuccess,
  onError,
}: Props<ExtractOptions<Parameters<UseQueryHook>[1]>> & {
  reactQuery: UseQueryHook;
  variables?: Parameters<UseQueryHook>[0];
  options?: Omit<Parameters<UseQueryHook>[1], 'queryKey'>;
}) {
  const {data, isFetching, isLoading, error} = reactQuery(variables, options);

  const onSuccessRef = useWrapRef(onSuccess);
  const onErrorRef = useWrapRef(onError);

  useRemoveCacheAfterUnmount({
    shouldRemoveCacheAfterUnmount,
    variables,
    getKey: reactQuery.getKey,
  });

  useEffect(() => {
    if (data && onSuccessRef.current) {
      onSuccessRef.current(data);
    }
  }, [data, onSuccessRef]);

  useEffect(() => {
    if (error && onErrorRef.current) {
      onErrorRef.current(error);
    }
  }, [error, onErrorRef]);

  return (
    <ReactQueryFetcherChildren
      isFetching={isFetching}
      isLoading={isLoading}
      ignoreFetching={ignoreFetching}
      ignoreLoading={ignoreLoading}
      data={data}
      options={options}
      loadingWrapperClassName={loadingWrapperClassName}
    >
      {children}
    </ReactQueryFetcherChildren>
  );
}
